/* Serial Seal — procedural security-pattern engine ("guilloche").
 *
 * WHY THIS EXISTS: a seal that can be redrawn in Canva is worthless. Real
 * security printing (banknotes, share certificates, passports) defends itself
 * with engine-turned lathe work: thousands of mathematically-generated points
 * at sub-0.1mm precision, parameterised per issuer. You cannot eyeball it,
 * you cannot trace it from a photo, and a photocopy loses the fine lines.
 *
 * Every function here is DETERMINISTIC: same seed string in → identical
 * geometry out. That is load-bearing — the print side (Label Farm, Python)
 * must port these exact formulas so preview == print, and so a seal can be
 * re-rendered years later to compare against a suspect one.
 *
 * The five defences:
 *   1. ROSETTE   — hypotrochoid lathe work; parameters derive from the
 *                  customer's brand+run, so no two issuers share a curve.
 *   2. BAND      — interwoven ribbon guilloche (the banknote border braid).
 *   3. RASTER    — fine-line ground at a frequency chosen to alias against
 *                  scanner/copier sampling: a copy shows banding the original
 *                  doesn't. This is the copy trap.
 *   4. LATENT    — a region of that raster phase-shifted 180°. Invisible flat,
 *                  it separates under a copy or a raking-light photo.
 *   5. MICROTEXT — the serial repeated at ~0.5mm; reads as a hairline rule to
 *                  the naked eye, resolves under a loupe. Serial-specific, so
 *                  a counterfeiter must redraw it per seal.
 *
 * PRINT REALITY: Epson C6000 at 1200dpi = one dot ≈ 0.021mm. Strokes below
 * ~0.05mm drop out; microtext below ~0.45mm turns to mush on vinyl. Defaults
 * here sit just above those floors. TEST PRINT before selling a new pattern.
 */
window.SSG = (function () {
  'use strict';

  var TAU = Math.PI * 2;

  /* ---------- deterministic randomness ---------- */
  function seed32(str) {
    var h = 0x811c9dc5; str = String(str == null ? '' : str);
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    return h >>> 0;
  }
  function rng(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function gcd(a, b) { while (b) { var t = b; b = a % b; a = t; } return a; }
  function r2(n) { return Math.round(n * 100) / 100; }
  function xesc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ---------- the issuer signature ----------
   * Derived once from a seed string (brand|run). Every pattern on the seal
   * reads from this object, so one brand's whole seal is a coherent family
   * and a different brand's is visibly, measurably different. */
  function signature(seedStr) {
    var r = rng(seed32(seedStr || 'serialseal'));
    var pick = function (a) { return a[Math.floor(r() * a.length)]; };
    var sig = {
      R: pick([11, 13, 17, 19, 23]),        // fixed circle (coprime-ish set)
      r: pick([3, 4, 5, 6, 7]),             // rolling circle
      dRatio: 0.55 + r() * 0.42,            // pen offset → petal sharpness
      layers: 3 + Math.floor(r() * 3),      // concentric lathe passes
      twist: r() * TAU,                     // rosette rotation
      spread: 0.05 + r() * 0.09,            // per-layer radius creep
      bandF1: 2 + Math.floor(r() * 3),      // ribbon carrier frequency
      bandF2: 5 + Math.floor(r() * 6),      // ribbon modulator
      bandPhase: r() * TAU,
      ribbons: 4 + Math.floor(r() * 3),
      rasterF: 1.4 + r() * 1.8,             // ground wave frequency
      rasterPhase: r() * TAU
    };
    if (gcd(sig.R, sig.r) !== 1) sig.r = (sig.r === 4 ? 5 : sig.r + 1); // keep petals clean
    return sig;
  }

  /* ---------- 1. rosette (hypotrochoid lathe work) ----------
   * x = (R−r)cos t + d cos(((R−r)/r) t)
   * y = (R−r)sin t − d sin(((R−r)/r) t)
   * Closes after r/gcd(R,r) turns; petal count = R/gcd(R,r). */
  function rosette(cx, cy, radius, sig, o) {
    o = o || {};
    var q = o.q == null ? 1 : o.q;
    var col = o.color || '#111116';
    var sw = o.stroke || 0.07;
    var g = gcd(sig.R, sig.r), turns = sig.r / g;
    var base = sig.R - sig.r;
    var layers = Math.max(2, Math.round((o.layers || sig.layers) * (q < 1 ? 0.55 : 1)));
    var steps = Math.max(120, Math.round(turns * 170 * q));
    var out = '';
    for (var L = 0; L < layers; L++) {
      var d = base * (sig.dRatio + L * sig.spread);
      var s = radius / (base + d);
      var k = base / sig.r;
      var p = '';
      for (var i = 0; i <= steps; i++) {
        var t = (i / steps) * turns * TAU;
        var x = s * (base * Math.cos(t) + d * Math.cos(k * t));
        var y = s * (base * Math.sin(t) - d * Math.sin(k * t));
        p += (i ? 'L' : 'M') + r2(cx + x) + ' ' + r2(cy + y);
      }
      var rot = (sig.twist * 180 / Math.PI) + L * (o.layerTwist == null ? 4.5 : o.layerTwist);
      var op = o.fade ? (1 - L * 0.13) : 1;
      out += '<path d="' + p + 'Z" fill="none" stroke="' + col + '" stroke-width="' +
             r2(sw * (1 - L * 0.06)) + '" opacity="' + r2(op) + '" transform="rotate(' +
             r2(rot) + ' ' + r2(cx) + ' ' + r2(cy) + ')"/>';
    }
    return out;
  }

  /* ---------- 2. interwoven ribbon band ---------- */
  function band(x0, x1, yc, amp, sig, o) {
    o = o || {};
    var q = o.q == null ? 1 : o.q;
    if (q < 0.6) return '';                       // same rule as raster(): 0.055mm strokes are edge fuzz on thumbnails
    var col = o.color || '#111116';
    var sw = o.stroke || 0.06;
    var n = Math.max(3, Math.round((o.ribbons || sig.ribbons) * (q < 1 ? 0.6 : 1)));
    var steps = Math.max(60, Math.round((x1 - x0) * 9 * q));
    var vert = !!o.vertical;   // when true, x0/x1 are Y bounds and yc is the X centre
    var out = '';
    for (var i = 0; i < n; i++) {
      var ph = sig.bandPhase + i * TAU / n;
      var p = '';
      for (var j = 0; j <= steps; j++) {
        var u = j / steps;
        var a = u * TAU;
        var env = o.taper ? Math.pow(Math.sin(Math.PI * u), 0.45) : 1;
        var off = amp * env * Math.sin(sig.bandF1 * a + ph) *
                  (0.62 + 0.38 * Math.sin(sig.bandF2 * a + ph * 0.5));
        var along = x0 + (x1 - x0) * u;
        p += (j ? 'L' : 'M') + (vert ? r2(yc + off) + ' ' + r2(along)
                                     : r2(along) + ' ' + r2(yc + off));
      }
      out += '<path d="' + p + '" fill="none" stroke="' + col + '" stroke-width="' +
             r2(sw) + '" opacity="' + r2(o.opacity == null ? 1 : o.opacity) + '"/>';
    }
    return out;
  }

  /* ---------- 3 + 4. fine-line raster with latent phase panel ----------
   * `latent` = [x,y,w,h] inside the raster whose lines invert phase. Flat to
   * the eye; a copier's sampling grid pulls it out. */
  function raster(x, y, w, h, sig, o) {
    o = o || {};
    var q = o.q == null ? 1 : o.q;
    if (q < 0.6) return '';                       // skip on thumbnails — pure noise at that size
    var col = o.color || '#111116';
    var sw = o.stroke || 0.055;
    var sp = o.spacing || 0.3;
    var amp = o.amp == null ? 0.16 : o.amp;
    var ph = sig.rasterPhase + (o.phase || 0);
    var f = sig.rasterF * (o.freq || 1);
    var lat = o.latent;
    var steps = Math.max(24, Math.round(w * 5 * q));
    var out = '<g opacity="' + (o.opacity == null ? 0.85 : o.opacity) + '">';
    for (var yy = y + sp; yy < y + h; yy += sp) {
      var p = '', inLat = lat && yy >= lat[1] && yy <= lat[1] + lat[3];
      for (var j = 0; j <= steps; j++) {
        var u = j / steps, xx = x + w * u;
        var flip = (inLat && xx >= lat[0] && xx <= lat[0] + lat[2]) ? Math.PI : 0;
        var off = amp * Math.sin(f * u * TAU + ph + yy * 1.7 + flip);
        p += (j ? 'L' : 'M') + r2(xx) + ' ' + r2(yy + off);
      }
      out += '<path d="' + p + '" fill="none" stroke="' + col + '" stroke-width="' + r2(sw) + '"/>';
    }
    return out + '</g>';
  }

  /* ---------- 5. microtext ----------
   * Deliberately below the legibility floor: it should read as a rule.
   * Returns a single <text>; repeats `txt` to fill the width. */
  function microtext(x, y, w, txt, o) {
    o = o || {};
    var fs = o.size || 0.5;
    var ls = o.spacing == null ? 0.04 : o.spacing;
    var col = o.color || '#111116';
    var unit = String(txt) + (o.sep || ' · ');
    /* Helvetica averages ~0.52em, but the fallback face can be wider and
       letter-spacing adds up fast at this size. Overfill by a hair, then let
       the renderer's measured trim cut it back — textLength is unreliable in
       browsers and unsupported by svglib on the print side, so relying on it
       let the rule run off both edges of the seal. */
    var per = unit.length * (fs * 0.56 + ls);
    var reps = Math.max(1, Math.ceil(w / Math.max(0.01, per)));
    var s = '';
    for (var i = 0; i < reps; i++) s += unit;
    return '<text x="' + r2(x) + '" y="' + r2(y) + '" font-family="Helvetica" font-size="' +
      fs + '" letter-spacing="' + ls + '" fill="' + col + '" opacity="' +
      (o.opacity == null ? 0.9 : o.opacity) +
      (o.bold ? '" font-weight="bold' : '') +
      (o.anchor === 'middle' ? '" text-anchor="middle' : '') +
      '" data-micro="' + r2(w) + '" data-micro-sep="' +
      xesc((o.sep || ' · ').trim()) + '">' +
      xesc(s) + '</text>';
  }

  /* Vertical microtext rule (rotated) — for seal edges. */
  function microtextV(x, y0, h, txt, o) {
    o = o || {};
    return '<g transform="rotate(90 ' + r2(x) + ' ' + r2(y0) + ')">' +
      microtext(x, y0, h, txt, o) + '</g>';
  }

  /* ---------- guilloche corner medallion (small dense rosette) ---------- */
  function medallion(cx, cy, radius, sig, o) {
    o = o || {};
    return rosette(cx, cy, radius, sig, {
      q: o.q, color: o.color, stroke: o.stroke || 0.05,
      layers: o.layers || 3, layerTwist: 9, fade: true
    });
  }

  return {
    TAU: TAU, seed32: seed32, rng: rng, gcd: gcd, r2: r2, esc: xesc,
    signature: signature, rosette: rosette, band: band, raster: raster,
    microtext: microtext, microtextV: microtextV, medallion: medallion
  };
})();
